import { Fragment, type ReactNode } from 'react';

const MARCAS = {
  '*': 'b',
  _: 'i',
  '~': 's',
} as const;
type Marca = keyof typeof MARCAS;

const URL_RE = /\bhttps?:\/\/[^\s<]+[^\s<.,;:!?)"'\]]/g;
const ehLetra = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);

function acharFechamento(texto: string, marca: string, inicio: number): number {
  for (let j = inicio; j < texto.length; j++) {
    if (texto[j] === '\n') return -1;
    if (texto[j] !== marca) continue;
    if (j === inicio || /\s/.test(texto[j - 1]!)) continue;
    if (ehLetra(texto[j + 1])) continue;
    return j;
  }
  return -1;
}

function comLinks(texto: string, chave: string): ReactNode[] {
  const nos: ReactNode[] = [];
  let ultimo = 0;
  for (const m of texto.matchAll(URL_RE)) {
    if (m.index > ultimo) nos.push(texto.slice(ultimo, m.index));
    nos.push(
      <a key={`${chave}-l${m.index}`} href={m[0]} target="_blank" rel="noreferrer noopener">
        {m[0]}
      </a>,
    );
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) nos.push(texto.slice(ultimo));
  return nos;
}

function emLinha(texto: string, chave = 'f'): ReactNode[] {
  const nos: ReactNode[] = [];
  let solto = '';
  let i = 0;
  const soltar = () => {
    if (solto) nos.push(...comLinks(solto, `${chave}-${nos.length}`));
    solto = '';
  };

  while (i < texto.length) {
    const c = texto[i]!;

    if (texto.startsWith('```', i)) {
      const fim = texto.indexOf('```', i + 3);
      if (fim > i + 3) {
        soltar();
        nos.push(
          <code key={`${chave}-${i}`} className="fmt-mono">
            {texto.slice(i + 3, fim)}
          </code>,
        );
        i = fim + 3;
        continue;
      }
    }

    if (c === '`' && !ehLetra(texto[i - 1])) {
      const fim = texto.indexOf('`', i + 1);
      if (fim > i + 1 && !texto.slice(i + 1, fim).includes('\n')) {
        soltar();
        nos.push(
          <code key={`${chave}-${i}`} className="fmt-codigo">
            {texto.slice(i + 1, fim)}
          </code>,
        );
        i = fim + 1;
        continue;
      }
    }

    if (c in MARCAS && !ehLetra(texto[i - 1]) && texto[i + 1] && !/\s/.test(texto[i + 1]!)) {
      const fim = acharFechamento(texto, c, i + 1);
      if (fim > i + 1) {
        soltar();
        const Tag = MARCAS[c as Marca];
        nos.push(
          <Tag key={`${chave}-${i}`}>{emLinha(texto.slice(i + 1, fim), `${chave}-${i}`)}</Tag>,
        );
        i = fim + 1;
        continue;
      }
    }

    solto += c;
    i++;
  }
  soltar();
  return nos;
}

export function TextoWhatsapp({ texto }: { texto: string }) {
  const linhas = texto.split('\n');
  const blocos: ReactNode[] = [];
  let citacao: string[] = [];

  const fecharCitacao = (k: number) => {
    if (!citacao.length) return;
    blocos.push(
      <span key={`q${k}`} className="fmt-citacao">
        {emLinha(citacao.join('\n'), `q${k}`)}
      </span>,
    );
    citacao = [];
  };

  linhas.forEach((linha, k) => {
    const q = /^>\s?(.*)$/.exec(linha);
    if (q) {
      citacao.push(q[1]!);
      return;
    }
    fecharCitacao(k);
    blocos.push(
      <Fragment key={k}>
        {emLinha(linha, `l${k}`)}
        {k < linhas.length - 1 && '\n'}
      </Fragment>,
    );
  });
  fecharCitacao(linhas.length);
  return <>{blocos}</>;
}

export function semFormatacao(texto: string): string {
  return texto
    .replace(/```([\s\S]*?)```/g, '$1')
    .replace(/(^|[^\p{L}\p{N}])([*_~`])(\S(?:.*?\S)?)\2(?![\p{L}\p{N}])/gu, '$1$3')
    .replace(/^>\s?/gm, '');
}

export function envolverSelecao(
  el: HTMLTextAreaElement,
  marca: string,
  aplicar: (novo: string, cursorIni: number, cursorFim: number) => void,
) {
  const { selectionStart: ini, selectionEnd: fim, value } = el;
  const selecionado = value.slice(ini, fim);
  if (!selecionado) {
    aplicar(value.slice(0, ini) + marca + marca + value.slice(fim), ini + 1, ini + 1);
    return;
  }
  const novo = value.slice(0, ini) + marca + selecionado + marca + value.slice(fim);
  aplicar(novo, ini + 1, fim + 1);
}
